import { Badge, Button, Modal, Spinner } from "react-bootstrap";

import {
  canApproveRegistration,
  canUnapproveRegistration,
  flattenRegistrationDetails,
  getDriverImageUrl,
  getDriverName,
  getInheritedConsentInfo,
  getStatusBadgeVariant,
  isQualifiedRegistration,
} from "../utils/competitors.utils";

const RegistrationDetailModal = ({
  show,
  registration,
  categories = [],
  isLoading = false,
  canManage = false,
  approving = false,
  uploading = false,
  mode = "view",
  onClose,
  onApprove,
  onUnapprove,
  onDownloadConsent,
  onDownloadSignedConsent,
  onUploadSignedConsent,
  onOpenInspection,
  onOpenEdit,
}) => {
  const status = registration?.status || "";
  const consent = registration
    ? getInheritedConsentInfo(registration, categories)
    : null;
  const imageUrl = registration ? getDriverImageUrl(registration) : "";
  const id = registration?._id || registration?.id;
  const details = flattenRegistrationDetails(registration);
  const isEdit = mode === "edit";

  return (
    <Modal show={show} onHide={onClose} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          {isEdit ? "Edit competitor" : "Registration detail"}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {isLoading && !registration ? (
          <div className="d-flex justify-content-center py-4">
            <Spinner animation="border" />
          </div>
        ) : !registration ? (
          <p className="text-muted mb-0">Registration not found.</p>
        ) : (
          <>
            <div className="d-flex gap-3 align-items-start mb-4">
              <div
                className="d-flex align-items-center justify-content-center overflow-hidden border rounded"
                style={{ width: 72, height: 72, flexShrink: 0 }}
              >
                {imageUrl ? (
                  <img
                    src={imageUrl}
                    alt={getDriverName(registration)}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                ) : (
                  <span className="text-muted small">No photo</span>
                )}
              </div>
              <div>
                <h5 className="mb-1">{getDriverName(registration)}</h5>
                <div className="d-flex flex-wrap gap-2">
                  <Badge bg={getStatusBadgeVariant(status)}>
                    {status || "-"}
                  </Badge>
                  <Badge bg={consent?.isSigned ? "success" : "secondary"}>
                    {consent?.isSigned ? "Consent signed" : "Consent not signed"}
                  </Badge>
                  <Badge
                    bg={
                      isQualifiedRegistration(registration)
                        ? "success"
                        : "secondary"
                    }
                  >
                    {isQualifiedRegistration(registration)
                      ? "Qualified"
                      : "Not qualified"}
                  </Badge>
                </div>
              </div>
            </div>

            {isEdit ? (
              <div className="alert alert-info mb-4">
                Use the table for firing order and rally number. Below you can
                manage approval, inspection, and replace the category signed
                consent PDF.
              </div>
            ) : null}

            <div className="row">
              {details.map((row) => (
                <div className="col-md-6 mb-3" key={`${row.label}-${row.value}`}>
                  <div className="text-muted small mb-1">{row.label}</div>
                  <div>{row.value || "-"}</div>
                </div>
              ))}
            </div>
          </>
        )}
      </Modal.Body>
      <Modal.Footer className="flex-wrap gap-2">
        {consent?.hasConsentTemplate ? (
          <Button
            variant="outline-secondary"
            onClick={() => onDownloadConsent(registration)}
          >
            Download consent
          </Button>
        ) : null}
        {consent?.signedUrl ? (
          <Button
            variant="outline-secondary"
            onClick={() => onDownloadSignedConsent(consent.signedUrl)}
          >
            Download signed consent
          </Button>
        ) : null}
        {canManage && consent?.categoryId ? (
          <Button
            variant="outline-primary"
            disabled={uploading}
            onClick={() => onUploadSignedConsent(registration)}
          >
            {uploading
              ? "Uploading..."
              : consent.isSigned
                ? "Replace signed consent"
                : "Upload signed consent"}
          </Button>
        ) : null}
        {canManage && registration ? (
          <Button
            variant="outline-primary"
            onClick={() => onOpenInspection(registration)}
          >
            {registration.inspection || registration.inspected
              ? "Edit inspection"
              : "Open inspection"}
          </Button>
        ) : null}
        {!isEdit && canManage && registration ? (
          <Button variant="outline-dark" onClick={() => onOpenEdit(registration)}>
            Edit
          </Button>
        ) : null}
        {canManage && canApproveRegistration(status) ? (
          <Button
            variant="success"
            disabled={approving || !id}
            onClick={() => onApprove(registration)}
          >
            {approving ? "Approving..." : "Approve"}
          </Button>
        ) : null}
        {canManage && canUnapproveRegistration(status) ? (
          <Button
            variant="warning"
            disabled={approving || !id}
            onClick={() => onUnapprove(registration)}
          >
            {approving ? "Updating..." : "Set pending"}
          </Button>
        ) : null}
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default RegistrationDetailModal;
